import { useEffect, useRef, useState } from "react";
import {
  CheckSquare, Bell, Palette, Pin, X, Plus, Loader2, Archive, Trash2, Lock, Share2,
  MoreVertical, Info, ImagePlus, FilePlus2, Mic, Camera, Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { NOTE_COLORS, colorClass } from "@/lib/noteColors";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/LanguageContext";
import { uploadImage, uploadDocument, fileUrl, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { newGuestLock } from "@/lib/guestLock";
import { createLabel, emitChange } from "@/lib/offlineStore";
import { labelStyle } from "@/lib/labelColors";
import LabelPicker, { LabelPillTrigger } from "@/components/LabelPicker";
import LabelSuggestions from "@/components/LabelSuggestions";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import AudioRecorder from "@/components/AudioRecorder";
import { format } from "date-fns";

const emptyNote = () => ({
  title: "",
  content: "",
  is_checklist: false,
  checklist_items: [],
  color: "default",
  labels: [],
  pinned: false,
  reminder: null,
  images: [],
  attachments: [],
});

export default function QuickCreate({ onCreate, onArchive, onTrash, onShare, onLockApplied, labels = [] }) {
  const { t } = useT();
  const { authState } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(emptyNote());
  const [uploading, setUploading] = useState(false);
  const [dragImg, setDragImg] = useState(false);
  const [dragDoc, setDragDoc] = useState(false);
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const imgInputRef = useRef(null);
  const docInputRef = useRef(null);
  // Hidden camera input for the "take a picture" quick action.
  const cameraInputRef = useRef(null);
  const [audioOpen, setAudioOpen] = useState(false);
  // Suppress the auto-submit-on-outside-click one time, used when we fire
  // a save explicitly (archive/trash/lock/share) and don't want a second
  // save to run as the popover/dropdown closes and the outside click bubbles.
  const suppressSubmitRef = useRef(false);

  useEffect(() => {
    const onClick = (e) => {
      if (!expanded) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Popovers, dialogs and toasts render in portals outside the card.
        if (e.target.closest?.('[data-radix-popper-content-wrapper],[role="dialog"],[role="alertdialog"],[data-sonner-toaster]')) return;
        if (suppressSubmitRef.current) { suppressSubmitRef.current = false; return; }
        submit();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  });

  const expand = (checklist = false) => {
    setExpanded(true);
    setNote({ ...emptyNote(), is_checklist: checklist, checklist_items: checklist ? [{ id: cryptoId(), text: "", checked: false }] : [] });
    setTimeout(() => titleRef.current?.focus(), 10);
  };

  const hasContent = (n = note) => (
    (n.title || "").trim() ||
    (n.content || "").trim() ||
    (n.is_checklist && (n.checklist_items || []).some((i) => (i.text || "").trim())) ||
    (n.images && n.images.length > 0) ||
    (n.attachments && n.attachments.length > 0)
  );

  const buildPayload = (n = note) => {
    const p = { ...n };
    if (n.is_checklist) {
      p.checklist_items = (n.checklist_items || []).filter((i) => (i.text || "").trim());
    }
    return p;
  };

  const submit = async () => {
    if (hasContent()) await onCreate(buildPayload());
    setNote(emptyNote());
    setExpanded(false);
  };

  // Save + run a follow-up action (archive, trash, lock, share) on the
  // just-created note. Returns the saved note (or null if nothing to save).
  const saveThen = async (follow) => {
    suppressSubmitRef.current = true;
    if (!hasContent()) {
      toast.message("Add something to the note first");
      return null;
    }
    const created = await onCreate(buildPayload());
    setNote(emptyNote());
    setExpanded(false);
    if (created && follow) {
      try { await follow(created); } catch (err) { console.warn("[quickcreate] follow-up failed:", err?.message || err); }
    }
    return created;
  };

  const addImageFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f && f.type?.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of list) {
        try { const { path } = await uploadImage(file); paths.push(path); }
        catch (err) { console.warn("[quickcreate] image upload failed:", err?.message || err); toast.error("Image upload failed"); }
      }
      if (paths.length) {
        setNote((n) => ({ ...n, images: [...(n.images || []), ...paths] }));
        if (!expanded) setExpanded(true);
      }
    } finally { setUploading(false); }
  };

  const addDocFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of list) {
        try {
          const data = await uploadDocument(file);
          added.push({ path: data.path, filename: data.filename, size: data.size, content_type: data.content_type });
        } catch (err) { console.warn("[quickcreate] doc upload failed:", err?.message || err); toast.error("File upload failed"); }
      }
      if (added.length) {
        setNote((n) => ({ ...n, attachments: [...(n.attachments || []), ...added] }));
        if (!expanded) setExpanded(true);
      }
    } finally { setUploading(false); }
  };

  const updateItem = (id, patch) => {
    setNote((n) => ({
      ...n,
      checklist_items: n.checklist_items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));
  };
  const removeItem = (id) => {
    setNote((n) => ({ ...n, checklist_items: n.checklist_items.filter((i) => i.id !== id) }));
  };
  const addItem = () => {
    setNote((n) => ({
      ...n,
      checklist_items: [...n.checklist_items, { id: cryptoId(), text: "", checked: false }],
    }));
  };

  // ---------- footer actions on the (unsaved) draft ----------
  const toggleChecklistKind = () => {
    setNote((n) => {
      if (n.is_checklist) {
        // list -> text: fold items into one paragraph-per-line HTML
        const html = (n.checklist_items || [])
          .filter((i) => (i.text || "").trim())
          .map((i) => `<p>${(i.checked ? "✓ " : "") + escapeHtml(i.text || "")}</p>`)
          .join("") || "";
        return { ...n, is_checklist: false, checklist_items: [], content: html };
      }
      // text -> list: break by lines, strip tags
      const text = (n.content || "").replace(/<[^>]+>/g, "\n");
      const items = text.split(/\n/).map((s) => s.trim()).filter(Boolean)
        .map((s) => ({ id: cryptoId(), text: s, checked: false }));
      return {
        ...n, is_checklist: true,
        checklist_items: items.length ? items : [{ id: cryptoId(), text: "", checked: false }],
        content: "",
      };
    });
  };

  const archiveDraft = () => saveThen((created) => onArchive?.(created.id, true));
  const trashDraft = () => saveThen((created) => onTrash?.(created.id));
  const shareDraft = () => saveThen((created) => onShare?.(created));
  const lockDraft = async () => {
    const pass = window.prompt("Set a passphrase (3+ characters)");
    if (!pass || pass.length < 3) return;
    await saveThen(async (created) => {
      const mirror = (patch) => {
        const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
        localStorage.setItem("keepflow.notes", JSON.stringify(notes.map((n) => (n.id === created.id ? { ...n, ...patch } : n))));
        emitChange();
      };
      if (authState === "user") {
        try {
          const { data } = await api.post(`/notes/${created.id}/lock`, { passphrase: pass });
          mirror({ locked: true, content: "", checklist_items: [], updated_at: data?.updated_at || created.updated_at });
          toast.success("Note locked");
        }
        catch (err) { console.warn("[quickcreate] lock failed:", err?.message || err); toast.error("Lock failed"); return; }
      } else {
        try {
          const { salt, hash } = await newGuestLock(pass);
          mirror({ locked: true, guest_lock_salt: salt, guest_lock_hash: hash });
          toast.success("Note locked");
        } catch (err) { console.warn("[quickcreate] guest lock failed:", err?.message || err); toast.error("Lock failed"); return; }
      }
      onLockApplied?.(created.id);
    });
  };

  const toggleLabel = (name) => {
    const has = (note.labels || []).includes(name);
    setNote((n) => ({ ...n, labels: has ? n.labels.filter((l) => l !== name) : [...(n.labels || []), name] }));
  };
  const createAndAttach = async (name) => {
    try {
      const label = await createLabel(name);
      const labelName = label?.name || name;
      setNote((n) => ((n.labels || []).includes(labelName) ? n : { ...n, labels: [...(n.labels || []), labelName] }));
      toast.success(`Label "${labelName}" added`);
    } catch (err) {
      console.warn("[quickcreate] create label failed:", err?.message || err);
      toast.error("Couldn't create the label");
    }
  };

  // ---------- drag handlers for the top dropzones ----------
  const onImgDrop = (e) => {
    e.preventDefault(); setDragImg(false);
    if (e.dataTransfer?.files?.length) addImageFiles(e.dataTransfer.files);
  };
  const onDocDrop = (e) => {
    e.preventDefault(); setDragDoc(false);
    if (e.dataTransfer?.files?.length) addDocFiles(e.dataTransfer.files);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 pt-6 pb-2">
      <div
        ref={containerRef}
        className={cn(
          "rounded-2xl border shadow-sm transition-all",
          colorClass(note.color),
          expanded ? "shadow-md" : "hover:shadow-md"
        )}
        data-testid="quick-create-container"
      >
        {!expanded ? (
          <div className="flex items-center px-5 py-3">
            <button
              className="flex-1 text-left text-stone-500 dark:text-stone-400 text-sm sm:text-base py-2"
              onClick={() => expand(false)}
              data-testid="quick-create-input"
            >
              {t("quick.placeholder")}
            </button>
            <div className="flex items-center gap-1">
              <button
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                onClick={() => expand(true)}
                data-testid="quick-create-checklist-btn"
                aria-label={t("quick.newChecklist")}
                title="New checklist"
              >
                <CheckSquare className="h-4 w-4" />
              </button>
              <button
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                onClick={() => imgInputRef.current?.click()}
                data-testid="quick-create-image-btn"
                aria-label="Add image"
                title="Add image"
              >
                <ImagePlus className="h-4 w-4" />
              </button>
              <button
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                onClick={() => docInputRef.current?.click()}
                data-testid="quick-create-doc-btn"
                aria-label="Add file"
                title="Add file"
              >
                <FilePlus2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {/* --- Dropzones ABOVE the title, side-by-side --- */}
            <div className="hidden md:grid grid-cols-2 gap-2" data-testid="quick-create-dropzones">
              <button
                type="button"
                onClick={() => imgInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragImg(true); }}
                onDragLeave={() => setDragImg(false)}
                onDrop={onImgDrop}
                className={cn(
                  "flex items-center justify-center gap-2 border-2 border-dashed rounded-xl text-xs sm:text-sm px-3 py-2.5 cursor-pointer transition-colors",
                  dragImg
                    ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-200"
                    : "border-stone-300/70 dark:border-stone-700 text-stone-500 dark:text-stone-400 hover:border-stone-400 dark:hover:border-stone-600"
                )}
                data-testid="quick-create-image-dropzone"
                aria-label="Drop image or click"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                <span className="truncate">{dragImg ? "Drop image here" : "Drop image or click"}</span>
              </button>
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragDoc(true); }}
                onDragLeave={() => setDragDoc(false)}
                onDrop={onDocDrop}
                className={cn(
                  "flex items-center justify-center gap-2 border-2 border-dashed rounded-xl text-xs sm:text-sm px-3 py-2.5 cursor-pointer transition-colors",
                  dragDoc
                    ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-200"
                    : "border-stone-300/70 dark:border-stone-700 text-stone-500 dark:text-stone-400 hover:border-stone-400 dark:hover:border-stone-600"
                )}
                data-testid="quick-create-doc-dropzone"
                aria-label="Drop file or click"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
                <span className="truncate">{dragDoc ? "Drop file here" : "Drop file or click"}</span>
              </button>
            </div>

            {/* Thumbs / chips for already-attached media. */}
            {(note.images?.length > 0 || note.attachments?.length > 0) && (
              <div className="flex flex-wrap gap-1.5" data-testid="quick-create-media">
                {note.images.map((p) => (
                  <div key={p} className="relative h-14 w-14 rounded-lg overflow-hidden border border-stone-200 dark:border-stone-700 group">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setNote((n) => ({ ...n, images: n.images.filter((x) => x !== p) }))}
                      className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition inline-flex items-center justify-center"
                      aria-label="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {note.attachments.map((a) => (
                  <span key={a.path} className="inline-flex items-center gap-1 rounded-full bg-stone-200/60 dark:bg-stone-700/60 text-xs px-2 py-1 max-w-[200px]">
                    <span className="truncate">📎 {a.filename}</span>
                    <button
                      type="button"
                      onClick={() => setNote((n) => ({ ...n, attachments: n.attachments.filter((x) => x.path !== a.path) }))}
                      className="h-4 w-4 rounded-full hover:bg-stone-300/60 inline-flex items-center justify-center"
                      aria-label="Remove file"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                ref={titleRef}
                placeholder={t("quick.title")}
                value={note.title}
                onChange={(e) => setNote({ ...note, title: e.target.value })}
                className="border-0 shadow-none focus-visible:ring-0 px-0 text-base font-semibold bg-transparent"
                data-testid="quick-create-title"
              />
              <button
                onClick={() => setNote({ ...note, pinned: !note.pinned })}
                className={cn("h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                  note.pinned && "text-amber-700 dark:text-amber-300")}
                data-testid="quick-create-pin-btn"
                aria-label="Pin"
                title="Pin"
              >
                <Pin className={cn("h-4 w-4", note.pinned && "fill-current")} />
              </button>
            </div>

            {note.is_checklist ? (
              <div className="space-y-1.5">
                {note.checklist_items.map((it, idx) => (
                  <ChecklistRow
                    key={it.id}
                    item={it}
                    autoFocus={idx === note.checklist_items.length - 1 && !it.text}
                    onChange={(patch) => updateItem(it.id, patch)}
                    onRemove={() => removeItem(it.id)}
                    onEnter={addItem}
                  />
                ))}
              </div>
            ) : (
              <RichTextEditor
                value={note.content}
                onChange={(html) => setNote({ ...note, content: html })}
                placeholder={t("quick.placeholder")}
                testid="quick-create-content"
                minHeight="90px"
              />
            )}

            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="quick-create-label-chips">
              {(note.labels || []).map((l) => (
                <span key={l} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${labelStyle(labels, l).chip}`}>
                  {l}
                  <button onClick={() => toggleLabel(l)} className="opacity-60 hover:opacity-100" aria-label={`Remove label ${l}`} data-testid={`quick-create-label-chip-remove-${l}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              <LabelPicker labels={labels} selected={note.labels || []} onToggle={toggleLabel} onCreate={createAndAttach} testId="quick-create-label-picker"
                trigger={<LabelPillTrigger count={(note.labels || []).length} data-testid="quick-create-add-label" />} />
            </div>
            <LabelSuggestions
              title={note.title}
              content={note.content}
              labels={labels}
              selected={note.labels || []}
              onAccept={(s) => {
                const match = labels.find((l) => l.name.toLowerCase() === s.name.toLowerCase());
                if (match) { if (!(note.labels || []).includes(match.name)) toggleLabel(match.name); }
                else createAndAttach(s.name);
              }}
            />

            {/* --- Full toolbar (mirrors NoteEditor) --- */}
            <div className="flex items-center gap-2 pt-2 border-t border-border mt-2">
              <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0 py-0.5">
                <ColorPicker
                  current={note.color}
                  onPick={(k) => setNote({ ...note, color: k })}
                />
                <ReminderPicker
                  value={note.reminder}
                  onChange={(d) => setNote({ ...note, reminder: d })}
                />
                <LabelPicker labels={labels} selected={note.labels || []} onToggle={toggleLabel} onCreate={createAndAttach} testId="quick-create-labels-menu"
                  trigger={
                    <button
                      className={cn(
                        "relative h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                        (note.labels || []).length > 0 && "text-amber-700 dark:text-amber-300"
                      )}
                      data-testid="quick-create-labels"
                      aria-label="Labels"
                      title="Add labels"
                    >
                      <Tag className="h-4 w-4" />
                      {(note.labels || []).length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9px] leading-[14px] text-center">{note.labels.length}</span>
                      )}
                    </button>
                  } />
                <button
                  onClick={toggleChecklistKind}
                  className="h-8 sm:h-9 shrink-0 px-2 sm:px-3 rounded-full text-xs sm:text-sm hover:bg-stone-200/60 dark:hover:bg-stone-700/60 whitespace-nowrap"
                  data-testid="quick-create-toggle-checklist"
                >
                  {note.is_checklist ? t("editor.toText") : t("editor.toChecklist")}
                </button>
                <button
                  onClick={() => imgInputRef.current?.click()}
                  className="md:hidden h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-mobile-image"
                  aria-label="Add image"
                  title="Add image"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => docInputRef.current?.click()}
                  className="md:hidden h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-mobile-doc"
                  aria-label="Add file"
                  title="Add file"
                >
                  <FilePlus2 className="h-4 w-4" />
                </button>
                {/* Mic — opens AudioRecorder; the resulting attachment is
                    appended to the draft so it gets saved on close. */}
                <button
                  onClick={() => { suppressSubmitRef.current = true; setAudioOpen(true); }}
                  className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-mic"
                  aria-label="Record voice note"
                  title="Record voice note"
                >
                  <Mic className="h-4 w-4" />
                </button>
                {/* Camera — mobile only (desktop has the dropzones at
                    the top of the expanded form). */}
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="md:hidden h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-camera"
                  aria-label="Take a picture"
                  title="Take a picture"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <button
                  onClick={archiveDraft}
                  className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-archive"
                  aria-label="Archive"
                  title="Archive"
                >
                  <Archive className="h-4 w-4" />
                </button>
                <button
                  onClick={trashDraft}
                  className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-trash"
                  aria-label="Trash"
                  title="Trash"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <button
                  onClick={lockDraft}
                  className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-lock"
                  aria-label="Lock"
                  title="Lock with passphrase"
                >
                  <Lock className="h-4 w-4" />
                </button>
                <button
                  onClick={shareDraft}
                  className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                  data-testid="quick-create-share"
                  aria-label="Share"
                  title="Share this note"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
              <Button onClick={submit} data-testid="quick-create-save" className="rounded-full shrink-0 h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm">
                {t("quick.close")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs for the attach buttons / dropzones. */}
      <input
        ref={imgInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; addImageFiles(files); }}
        data-testid="quick-create-image-input"
      />
      <input
        ref={docInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; addDocFiles(files); }}
        data-testid="quick-create-doc-input"
      />
      {/* Camera input — `capture="environment"` opens the rear camera on
          phones. The result is uploaded through the same path as the
          image dropzone so thumbnails attach instantly. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; addImageFiles(files); }}
        data-testid="quick-create-camera-input"
      />
      <AudioRecorder
        open={audioOpen}
        onOpenChange={setAudioOpen}
        onSave={(att) => {
          setNote((n) => ({ ...n, attachments: [...(n.attachments || []), att] }));
          if (!expanded) setExpanded(true);
        }}
      />
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function safeNow() {
  try { return format(new Date(), "MMM d, HH:mm"); } catch { return "just now"; }
}

function ChecklistRow({ item, onChange, onRemove, onEnter, autoFocus }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange({ checked: !item.checked })}
        className={cn(
          "h-4 w-4 rounded border border-stone-400 dark:border-stone-500 flex items-center justify-center",
          item.checked && "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100"
        )}
        aria-label="Toggle"
      >
        {item.checked && <span className="text-[10px] text-white dark:text-stone-900">✓</span>}
      </button>
      <input
        value={item.text}
        onChange={(e) => onChange({ text: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onEnter && onEnter(); }
        }}
        autoFocus={autoFocus}
        className={cn(
          "flex-1 bg-transparent outline-none text-sm py-1",
          item.checked && "line-through text-stone-400"
        )}
      />
      <button
        onClick={onRemove}
        className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        aria-label="Remove item"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function ColorPicker({ current, onPick }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
          data-testid="color-picker-trigger"
          aria-label="Color"
        >
          <Palette className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="flex gap-1.5 flex-wrap max-w-[220px]">
          {NOTE_COLORS.map((c) => (
            <button
              key={c.key}
              onClick={() => onPick(c.key)}
              className={cn(
                "h-7 w-7 rounded-full border-2 transition-transform hover:scale-110",
                current === c.key ? "border-stone-900 dark:border-stone-100" : "border-stone-300 dark:border-stone-700"
              )}
              style={{ background: c.swatch }}
              title={c.name}
              data-testid={`color-swatch-${c.key}`}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ReminderPicker({ value, onChange }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value) : undefined;

  // Picks a date-only reminder; defaults to 9 AM so the server still has
  // a precise instant to send a notification at, but the user never has
  // to deal with time/repeat UI anymore (per product spec).
  const applyDate = (d) => {
    if (!d) { onChange(null); return; }
    const next = new Date(d);
    next.setHours(9, 0, 0, 0);
    onChange(next.toISOString());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
            value && "text-amber-700 dark:text-amber-300"
          )}
          data-testid="reminder-picker-trigger"
          aria-label="Reminder"
          title={value ? `Reminder: ${new Date(value).toLocaleDateString()}` : "Set reminder"}
        >
          <Bell className={cn("h-4 w-4", value && "fill-current")} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} onSelect={applyDate} initialFocus />
        {value && (
          <div className="px-3 pb-2 pt-1 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => { onChange(null); setOpen(false); }}
              data-testid="reminder-clear"
            >
              {t("quick.clearReminder")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function cryptoId() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}
